<template>
  <div ref="mapContainer" class="map-container" :class="{ 'fullscreen': isFullscreen }">
    <div ref="mapView" class="map-view"></div>
    <div class="postal-search-overlay">
      <input
        v-model="postalCodeSearch"
        type="text"
        inputmode="numeric"
        maxlength="5"
        class="postal-search-input"
        placeholder="Ir a código postal"
        @keydown.enter.prevent="zoomToPostalCode"
      />
      <button
        class="postal-search-button"
        :disabled="postalCodeSearching"
        @click="zoomToPostalCode"
        title="Buscar código postal"
      >
        <f7-icon :f7="postalCodeSearching ? 'arrow_2_circlepath' : 'search'"></f7-icon>
      </button>
    </div>
    <button class="fullscreen-button" @click="toggleFullscreen" :title="isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'">
      <f7-icon :f7="isFullscreen ? 'arrow_down_right_arrow_up_left' : 'arrow_up_left_arrow_down_right'"></f7-icon>
    </button>
    <div v-if="itemsSinDireccion.length > 0" class="map-alert" @click="mostrarItemsSinDireccion" style="cursor: pointer;">
      <f7-chip color="orange" text-color="white">
        <f7-icon f7="exclamationmark_triangle_fill"></f7-icon>
        {{ itemsSinDireccion.length }} {{ itemsSinDireccion.length === 1 ? 'registro sin dirección' : 'registros sin dirección' }}
      </f7-chip>
    </div>
    <div v-if="geocodificando" class="map-geocoding-progress">
      <f7-chip color="blue" text-color="white">
        <f7-icon f7="location_fill" class="rotating"></f7-icon>
        Geocodificando {{ geocodingProgress.current }}/{{ geocodingProgress.total }}
      </f7-chip>
    </div>

    <!-- Sheet para mostrar items sin dirección -->
    <f7-sheet
      class="items-sin-direccion-sheet"
      :opened="sheetOpened"
      @sheet:closed="sheetOpened = false"
      swipe-to-close
      backdrop
    >
      <f7-toolbar>
        <div class="left"></div>
        <div class="right">
          <f7-link sheet-close>Cerrar</f7-link>
        </div>
      </f7-toolbar>
      <f7-page-content>
        <f7-block-title>Registros sin dirección ({{ itemsSinDireccion.length }})</f7-block-title>
        <f7-list>
          <f7-list-item
            v-for="(item, idx) in itemsSinDireccion"
            :key="idx"
            :title="getItemTitle(item)"
            :after="getItemSubtitle(item)"
          >
            <template v-if="getItemDescription(item)" v-slot:text>
              {{ getItemDescription(item) }}
            </template>
          </f7-list-item>
        </f7-list>
        <f7-block v-if="itemsSinDireccion.length === 0">
          <p class="text-align-center">No hay registros sin dirección</p>
        </f7-block>
      </f7-page-content>
    </f7-sheet>
  </div>
</template>

<script>
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import GeoUtils from '../js/db/GeoUtils';
import { esCancelacion } from '../js/utils/sesionDePeticiones.js';

export default {
  name: 'rMapView',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    mapConfig: {
      type: Object,
      required: true
    },
    selectedDirectionIndex: {
      type: Number,
      default: 0
    },
    columns: {
      type: Array,
      default: () => []
    },
    popupActionsRenderer: {
      type: Function,
      default: null
    }
  },
  data() {
    return {
      map: null,
      markerClusterGroup: null,
      itemsSinCoordenadas: 0,
      itemsSinDireccion: [],
      debugInvalidSamples: 0,
      geocodificando: false,
      geocodingProgress: { current: 0, total: 0 },
      geocodingCache: {},
      sheetOpened: false,
      isFullscreen: false,
      postalCodeSearch: '',
      postalCodeSearching: false
    };
  },
  mounted() {
    this.initMap();
    // Escuchar cambios en el estado de fullscreen
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    if (this.map) {
      this.map.remove();
    }
    // Limpiar listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  watch: {
    items: {
      handler() {
        this.updateMapData();
      },
      deep: true
    },
    selectedDirectionIndex() {
      // Cuando cambia la dirección seleccionada, actualizar el mapa
      this.updateMapData();
    }
  },
  methods: {
    initMap() {
      const self = this;
      
      const centroInicial = self.mapConfig.centroInicial || {
        lat: 40.4168,
        lng: -3.7038,
        zoom: 6
      };

      console.log('[MAP DEBUG] initMap', {
        dirConfig: self.getCurrentDirectionConfig(),
        totalItemsIniciales: Array.isArray(self.items) ? self.items.length : 0,
        centroInicial
      });

      // Crear mapa Leaflet
      self.map = L.map(self.$refs.mapView, { zoomControl: false }).setView(
        [centroInicial.lat, centroInicial.lng],
        centroInicial.zoom
      );

      // Reubicar controles de zoom a la derecha, debajo del botón de fullscreen
      L.control.zoom({ position: 'topright' }).addTo(self.map);

      // Añadir capa de OpenStreetMap (gratuita, sin token requerido)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19
      }).addTo(self.map);

      // Crear grupo de clustering
      self.markerClusterGroup = L.markerClusterGroup({
        maxClusterRadius: self.mapConfig.clustering?.radius || 50,
        maxZoom: self.mapConfig.clustering?.maxZoom || 15,
        disableClusteringAtZoom: self.mapConfig.clustering?.maxZoom || 15
      });

      self.map.addLayer(self.markerClusterGroup);
      
      // Cargar datos iniciales
      self.updateMapData();
    },

    createCustomIcon(color = '#EF4444') {
      // Crear icono personalizado con SVG puro
      return L.divIcon({
        html: `
          <div style="
            display: flex;
            align-items: center;
            justify-content: center;
            width: 28px;
            height: 28px;
            background-color: ${color};
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          ">
            <svg viewBox="0 0 24 24" width="14" height="14" style="transform: rotate(45deg);">
              <circle cx="12" cy="10" r="3" fill="white"/>
              <path d="M12 2 C6.5 2 2 6.5 2 12 C2 20 12 28 12 28 C12 28 22 20 22 12 C22 6.5 17.5 2 12 2 Z" 
                    fill="none" stroke="white" stroke-width="1" vector-effect="non-scaling-stroke"/>
            </svg>
          </div>
        `,
        className: 'custom-marker-icon',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -28]
      });
    },

    getMarkerColor(item) {
      const self = this;
      const campoColor = self.mapConfig.campoColor;
      const coloresMap = self.mapConfig.coloresMap || [];
      const colorPorDefecto = self.mapConfig.colorPorDefecto || '#EF4444';
      
      if (!campoColor) {
        return colorPorDefecto;
      }
      
      const valor = self.getValor(item, campoColor);
      if (!valor) {
        return colorPorDefecto;
      }
      
      // Buscar el color en el mapeo
      const mapeo = coloresMap.find(m => m.valor === valor);
      return mapeo ? mapeo.color : colorPorDefecto;
    },

    /**
     * Obtiene la configuración de dirección activa.
     * Si mapConfig.direcciones existe (array), retorna la dirección seleccionada.
     * Si no existe, retrocompatibilidad: usa los campos root de mapConfig.
     */
    getCurrentDirectionConfig() {
      const self = this;
      
      if (self.mapConfig.direcciones && Array.isArray(self.mapConfig.direcciones)) {
        const dirConfig = self.mapConfig.direcciones[self.selectedDirectionIndex] || self.mapConfig.direcciones[0];
        return {
          campoLatitud: dirConfig.campoLatitud,
          campoLongitud: dirConfig.campoLongitud,
          camposDireccion: dirConfig.camposDireccion || []
        };
      }
      
      // Fallback a estructura legacy
      return {
        campoLatitud: self.mapConfig.campoLatitud,
        campoLongitud: self.mapConfig.campoLongitud,
        camposDireccion: self.mapConfig.camposDireccion || []
      };
    },

    updateMapData() {
      const self = this;
      if (!self.map || !self.markerClusterGroup) return;

      try {
        // Obtener configuración de dirección activa
        const dirConfig = self.getCurrentDirectionConfig();

        // Limpiar marcadores anteriores
        self.markerClusterGroup.clearLayers();
        
        let sinCoordenadas = 0;
        const itemsSinDir = [];
        let hasPuntos = false;
        let puntosValidos = 0;
        const bounds = L.latLngBounds();
        const totalItems = Array.isArray(self.items) ? self.items.length : 0;

        self.debugInvalidSamples = 0;
        if (totalItems > 0) {
          console.log('[MAP DEBUG] updateMapData.start', { totalItems, selectedDirectionIndex: self.selectedDirectionIndex });
        }

        self.items.forEach((item, idx) => {
          // Primero verificar si tiene coordenadas geocodificadas previamente
          let latRaw = item._geocoded_lat || self.getValor(item, dirConfig.campoLatitud);
          let lngRaw = item._geocoded_lng || self.getValor(item, dirConfig.campoLongitud);
          const lat = self.parseCoordinate(latRaw);
          const lng = self.parseCoordinate(lngRaw);

          if (lat !== null && lng !== null) {
            hasPuntos = true;
            puntosValidos++;
            const latlng = L.latLng(lat, lng);
            bounds.extend(latlng);

            // Obtener color del marcador según el valor del campo
            const markerColor = self.getMarkerColor(item);

            // Crear marcador con popup e icono personalizado
            const marker = L.marker(latlng, { icon: self.createCustomIcon(markerColor) });
            const popupContent = self.createPopupContent(item);
            marker.bindPopup(popupContent);
            if (self.popupActionsRenderer) {
              self.setupPopupActionListeners(marker, item);
            }
            
            self.markerClusterGroup.addLayer(marker);
          } else {
            sinCoordenadas++;
            itemsSinDir.push(item);
          }
        });

        self.itemsSinCoordenadas = sinCoordenadas;
        self.itemsSinDireccion = itemsSinDir;

        if (totalItems > 0) {
          console.log('[MAP DEBUG] updateMapData.end', {
            totalItems,
            puntosValidos,
            sinCoordenadas
          });
        }

        // Ajustar vista a los puntos
        if (hasPuntos && bounds.isValid()) {
          self.map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
        }

        // Geocodificar items que tienen dirección pero no coordenadas
        if (sinCoordenadas > 0 && self.mapConfig.autoGeocode !== false) {
          self.geocodeItemsWithAddress();
        }
      } catch (e) {
        console.error('[MAP] Error en updateMapData:', e);
      }
    },

    async geocodeItemsWithAddress() {
      const self = this;
      if (self.geocodificando) return;

      try {
        // Obtener configuración de dirección activa
        const dirConfig = self.getCurrentDirectionConfig();

        // Filtrar items sin coordenadas pero con dirección
        const itemsToGeocode = self.items.filter(item => {
          const lat = self.parseCoordinate(self.getValor(item, dirConfig.campoLatitud));
          const lng = self.parseCoordinate(self.getValor(item, dirConfig.campoLongitud));
          const direccion = self.getDireccionCompleta(item);
          return (lat === null || lng === null) && direccion;
        });

        if (itemsToGeocode.length === 0) {
          console.log('[MAP GEOCODING] No hay items con dirección para geocodificar');
          return;
        }

        // Sin límite: geocodificar todos los pendientes vía backend
        const itemsLimitados = itemsToGeocode;

        console.log(`[MAP GEOCODING] Iniciando geocodificación backend de ${itemsLimitados.length} items`);

        self.geocodificando = true;
        self.geocodingProgress = { current: 0, total: itemsLimitados.length };

        let geocodificadosExito = 0;
        let geocodificadosFallo = 0;

        const itemsConCP = [];
        itemsLimitados.forEach(item => {
          const direccion = self.getDireccionCompleta(item);
          const info = self.obtenerCodigoPostalYProvincia(direccion);
          if (info && info.codigoPostal) {
            const pais = (self.mapConfig.paisGeocodificacion || 'spain').toLowerCase();
            itemsConCP.push({ item, codigoPostal: info.codigoPostal, pais });
          } else {
            geocodificadosFallo++;
          }
        });

        if (itemsConCP.length === 0) {
          console.warn('[MAP GEOCODING] No hay items con código postal válido para geocodificación backend');
          return;
        }

        self.geocodingProgress.total = itemsConCP.length;

        const payload = itemsConCP.map(i => ({
          codigoPostal: i.codigoPostal,
          pais: i.pais
        }));

        const resultados = await GeoUtils.geocodificarLote({ direcciones: payload });
        self.geocodingProgress.current = itemsConCP.length;

        itemsConCP.forEach((itemData) => {
          const resultado = resultados.find(r =>
            r &&
            r.codigoPostal &&
            String(r.codigoPostal).toUpperCase() === String(itemData.codigoPostal).toUpperCase() &&
            self.parseCoordinate(r.latitud) !== null &&
            self.parseCoordinate(r.longitud) !== null
          );

          if (resultado) {
            const lat = self.parseCoordinate(resultado.latitud);
            const lng = self.parseCoordinate(resultado.longitud);

            self.$set(itemData.item, '_geocoded_lat', lat);
            self.$set(itemData.item, '_geocoded_lng', lng);

            // No llamar a addMarkerToMap manualmente - el watcher de items se encarga de actualizar el mapa
            self.itemsSinCoordenadas--;
            geocodificadosExito++;

            const index = self.itemsSinDireccion.indexOf(itemData.item);
            if (index > -1) {
              self.itemsSinDireccion.splice(index, 1);
            }
          } else {
            geocodificadosFallo++;
          }
        });

        console.log(`[MAP GEOCODING] Resumen: ${geocodificadosExito} exitosos, ${geocodificadosFallo} fallidos de ${itemsLimitados.length} totales`);
      } catch (e) {
        console.error('[MAP] Error en geocodeItemsWithAddress:', e);
      } finally {
        self.geocodificando = false;
      }
    },

    addMarkerToMap(lat, lon, item) {
      const self = this;
      try {
        const latlng = L.latLng(parseFloat(lat), parseFloat(lon));
        const markerColor = self.getMarkerColor(item);
        const marker = L.marker(latlng, { icon: self.createCustomIcon(markerColor) });
        const popupContent = self.createPopupContent(item);
        marker.bindPopup(popupContent);
        if (self.popupActionsRenderer) {
          self.setupPopupActionListeners(marker, item);
        }
        self.markerClusterGroup.addLayer(marker);
      } catch (e) {
        console.error('[MAP] Error añadiendo marcador:', e);
      }
    },

    parseCoordinate(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }
      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null;
      }
      if (typeof value === 'string') {
        const normalized = value.trim().replace(',', '.');
        if (!normalized) return null;
        const parsed = Number(normalized);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    },

    normalizarDireccionParaGeocodificacion(direccion) {
      if (!direccion) return '';
      
      let normalized = String(direccion);
      
      // Reemplazar caracteres problemáticos
      normalized = normalized
        .replace(/\bNº\b/gi, '') // Remover Nº
        .replace(/\bS\/N\b/gi, '') // Remover S/N
        .replace(/ª/g, 'a') // Remover ordinal femenino
        .replace(/º/g, '') // Remover ordinal masculino
        .replace(/(\d+)-[A-Z]+/g, '$1') // Remover letras después de números (22-B → 22)
        .replace(/,\s*,/g, ',') // Remover comas dobles
        .replace(/\s+/g, ' ') // Normalizar espacios múltiples
        .trim();
      
      // Convertir a minúsculas para mejor búsqueda en Nominatim
      normalized = normalized.toLowerCase();
      
      // Agregar "spain" al final si no está
      if (!normalized.match(/spain|españa|es\b/i)) {
        normalized += ', spain';
      }
      
      console.log(`[MAP GEOCODING] Dirección normalizada: "${direccion}" → "${normalized}"`);
      return normalized;
    },

    // Extraer información de la dirección para fallback de búsqueda
    obtenerCodigoPostalYProvincia(direccion) {
      if (!direccion) return null;
      // Busca primer CP de 5 dígitos en cualquier posición
      const cpMatch = String(direccion).match(/\b(\d{5})\b/);
      if (cpMatch) {
        return {
          codigoPostal: cpMatch[1],
          provincia: ''
        };
      }
      return null;
    },

    getLatLngFromItem(item, dirConfig) {
      const self = this;
      const latRaw = item._geocoded_lat || self.getValor(item, dirConfig.campoLatitud);
      const lngRaw = item._geocoded_lng || self.getValor(item, dirConfig.campoLongitud);
      const lat = self.parseCoordinate(latRaw);
      const lng = self.parseCoordinate(lngRaw);
      if (lat === null || lng === null) return null;
      return L.latLng(lat, lng);
    },

    showMapToast(text, color = 'blue') {
      const self = this;
      if (!self.$f7 || !self.$f7.toast) return;
      self.$f7.toast
        .create({
          text,
          position: 'top',
          closeTimeout: 2500,
          cssClass: color === 'red' ? 'toast-red' : ''
        })
        .open();
    },

    async zoomToPostalCode() {
      const self = this;
      if (!self.map) return;

      const codigoPostal = String(self.postalCodeSearch || '').replace(/\D/g, '').slice(0, 5);
      if (!/^\d{5}$/.test(codigoPostal)) {
        self.showMapToast('Introduce un código postal válido (5 dígitos)', 'red');
        return;
      }

      const dirConfig = self.getCurrentDirectionConfig();
      const matchedPoints = [];

      self.items.forEach(item => {
        const direccion = self.getDireccionCompleta(item, dirConfig);
        const info = self.obtenerCodigoPostalYProvincia(direccion);
        if (info && info.codigoPostal === codigoPostal) {
          const latlng = self.getLatLngFromItem(item, dirConfig);
          if (latlng) matchedPoints.push(latlng);
        }
      });

      if (matchedPoints.length > 0) {
        if (matchedPoints.length === 1) {
          self.map.setView(matchedPoints[0], 15, { animate: true });
        } else {
          const bounds = L.latLngBounds(matchedPoints);
          self.map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
        }
        self.showMapToast(`Zoom aplicado al CP ${codigoPostal}`);
        return;
      }

      self.postalCodeSearching = true;
      try {
        const pais = (self.mapConfig.paisGeocodificacion || 'spain').toLowerCase();
        const resultados = await GeoUtils.geocodificarLote({
          direcciones: [{ codigoPostal, pais }]
        });

        const resultadoValido = (Array.isArray(resultados) ? resultados : []).find(r =>
          r && self.parseCoordinate(r.latitud) !== null && self.parseCoordinate(r.longitud) !== null
        );

        if (!resultadoValido) {
          self.showMapToast(`No se encontró ubicación para el CP ${codigoPostal}`, 'red');
          return;
        }

        const lat = self.parseCoordinate(resultadoValido.latitud);
        const lng = self.parseCoordinate(resultadoValido.longitud);
        self.map.setView([lat, lng], 13, { animate: true });
        self.showMapToast(`Centrado en CP ${codigoPostal}`);
      } catch (e) {
        // #663 · El `await` sí recibe la cancelación de cerrar sesión: no es un error que contar.
        if (esCancelacion(e)) return;
        console.error('[MAP] Error buscando código postal:', e);
        self.showMapToast('Error buscando el código postal', 'red');
      } finally {
        self.postalCodeSearching = false;
      }
    },

    logMapDebug(stage, payload) {
      console.log(`[MAP DEBUG] ${stage}`, payload);
    },

    createPopupContent(item) {
      const self = this;
      try {
        let html = '<div class="map-popup">';

        // Título
        const titulo = self.getValor(item, self.mapConfig.campoTitulo);
        if (titulo) {
          html += `<h3>${self.escapeHtml(titulo)}</h3>`;
        }

        // Campos del popup
        if (self.mapConfig.camposPopup && self.mapConfig.camposPopup.length) {
          html += '<div class="popup-fields">';
          self.mapConfig.camposPopup.forEach(campo => {
            if (campo.compuesto && campo.campos) {
              // Campo compuesto (dirección)
              const valores = campo.campos
                .map(c => self.getValor(item, c))
                .filter(v => v)
                .map(v => self.escapeHtml(v));
              if (valores.length > 0) {
                html += `<div class="popup-field">`;
                html += `<strong>${self.escapeHtml(campo.label)}:</strong> ${valores.join(', ')}`;
                html += `</div>`;
              }
            } else {
              // Campo simple
              const valor = self.getValor(item, campo.campo);
              if (valor) {
                let valorFormateado = valor;
                try {
                  if (campo.tipo === 'FECHA' || campo.tipo === 'FECHAHORA') {
                    if (self.$options.filters && self.$options.filters.dateFormat && self.$options.filters.dateParse) {
                      valorFormateado = self.$options.filters.dateFormat(
                        self.$options.filters.dateParse(valor),
                        campo.formato || 'DD/MM/YYYY'
                      );
                    }
                  }
                } catch (e) {
                  console.warn('[MAP] Error formateando fecha:', e);
                }
                html += `<div class="popup-field">`;
                html += `<strong>${self.escapeHtml(campo.label)}:</strong> ${self.escapeHtml(valorFormateado)}`;
                html += `</div>`;
              }
            }
          });
          html += '</div>';
        }

        // Dirección completa si está configurada
        const dirConfig = self.getCurrentDirectionConfig();
        if (dirConfig.camposDireccion && dirConfig.camposDireccion.length > 0) {
          const direccion = self.getDireccionCompleta(item, dirConfig);
          if (direccion) {
            html += `<div class="popup-address"><strong>📍</strong> ${self.escapeHtml(direccion)}</div>`;
          }
        }

        // Acciones del popup (proporcionadas por el padre)
        if (self.popupActionsRenderer) {
          try {
            const actionsHtml = self.popupActionsRenderer(item);
            if (actionsHtml) {
              html += `<div class="popup-actions">${actionsHtml}</div>`;
            }
          } catch (e) {
            console.error('[MAP] Error renderizando acciones:', e);
          }
        }

        html += '</div>';
        return html;
      } catch (e) {
        console.error('[MAP] Error creando popup:', e);
        return '<div class="map-popup"><p>Error al crear popup</p></div>';
      }
    },

    setupPopupActionListeners(marker, item) {
      const self = this;
      // Escuchar cuando se abre el popup para agregar event listeners a los botones
      marker.on('popupopen', function() {
        setTimeout(() => {
          const popup = marker.getPopup();
          // Intentar obtener el contenedor del popup de varias formas
          let popupContainer = null;
          if (popup) {
            popupContainer = popup._contentNode || popup._content || popup._container || popup.getElement();
          }

          if (popupContainer) {
            const actionButtons = popupContainer.querySelectorAll('[data-action]');
            actionButtons.forEach(button => {
              button.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const action = button.getAttribute('data-action');
                const id = button.getAttribute('data-id');
                console.log('[MAP] Acción clickeada:', { action, id, item });
                self.$emit('map-action', { actionId: action, actionData: id, item });
              });
            });
          }
        }, 50);
      });
    },

    getDireccionCompleta(item, dirConfig = null) {
      const self = this;
      
      // Si no se pasa dirConfig, obtenerla de la selección actual
      if (!dirConfig) {
        dirConfig = self.getCurrentDirectionConfig();
      }
      
      const campos = dirConfig.camposDireccion || 
                     self.mapConfig.direccionCompuesta?.campos || 
                     [];
      const separador = self.mapConfig.direccionCompuesta?.separador || ', ';

      return campos
        .map(campo => self.getValor(item, campo))
        .filter(v => v)
        .join(separador);
    },

    getValor(item, pathName) {
      if (!pathName) return null;
      const keys = pathName.split('.');
      let value = item;
      for (const key of keys) {
        if (value && typeof value === 'object') {
          value = value[key];
        } else {
          return null;
        }
      }
      return value;
    },

    escapeHtml(text) {
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return String(text).replace(/[&<>"']/g, m => map[m]);
    },

    mostrarItemsSinDireccion() {
      const self = this;
      self.sheetOpened = true;
    },

    getItemTitle(item) {
      const self = this;
      if (self.mapConfig.campoTitulo) {
        const titulo = self.getValor(item, self.mapConfig.campoTitulo);
        return titulo || 'Sin título';
      }
      return 'Registro';
    },

    getItemSubtitle(item) {
      const self = this;
      // Intentar obtener un campo identificador adicional
      if (self.mapConfig.camposPopup && self.mapConfig.camposPopup.length > 0) {
        const primerCampo = self.mapConfig.camposPopup[0];
        if (primerCampo && primerCampo.campo) {
          const valor = self.getValor(item, primerCampo.campo);
          if (valor) return String(valor);
        }
      }
      return '';
    },

    getItemDescription(item) {
      const self = this;
      // Intentar mostrar la dirección si existe
      const direccion = self.getDireccionCompleta(item);
      if (direccion) {
        return direccion;
      }
      
      // Si no hay dirección, mostrar otros campos configurados
      if (self.mapConfig.camposPopup && self.mapConfig.camposPopup.length > 1) {
        const segundoCampo = self.mapConfig.camposPopup[1];
        if (segundoCampo && segundoCampo.campo) {
          const valor = self.getValor(item, segundoCampo.campo);
          if (valor) return String(valor);
        }
      }
      return '';
    },

    toggleFullscreen() {
      const container = this.$refs.mapContainer;
      
      if (!document.fullscreenElement && 
          !document.webkitFullscreenElement && 
          !document.mozFullScreenElement && 
          !document.msFullscreenElement) {
        // Entrar en pantalla completa
        if (container.requestFullscreen) {
          container.requestFullscreen();
        } else if (container.webkitRequestFullscreen) {
          container.webkitRequestFullscreen();
        } else if (container.mozRequestFullScreen) {
          container.mozRequestFullScreen();
        } else if (container.msRequestFullscreen) {
          container.msRequestFullscreen();
        }
        this.isFullscreen = true;
      } else {
        // Salir de pantalla completa
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
        this.isFullscreen = false;
      }
      
      // Forzar al mapa a recalcular su tamaño
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    },

    handleFullscreenChange() {
      // Detectar si estamos en fullscreen o no
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement || 
        document.webkitFullscreenElement || 
        document.mozFullScreenElement || 
        document.msFullscreenElement
      );
      
      this.isFullscreen = isCurrentlyFullscreen;
      
      // Recalcular tamaño del mapa
      this.$nextTick(() => {
        if (this.map) {
          this.map.invalidateSize();
        }
      });
    }
  }
};
</script>

<style scoped>
.map-container {
  position: relative;
  width: 100%;
  min-height: calc(100vh - 200px);
  height: 100%;
  background: white;
}

/* Estilos cuando está en fullscreen nativo */
.map-container:fullscreen {
  width: 100vw;
  height: 100vh;
}

.map-container:-webkit-full-screen {
  width: 100vw;
  height: 100vh;
}

.map-container:-moz-full-screen {
  width: 100vw;
  height: 100vh;
}

.map-container:-ms-fullscreen {
  width: 100vw;
  height: 100vh;
}

.map-view {
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 200px);
}

.postal-search-overlay {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(4px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
}

.postal-search-input {
  width: 170px;
  height: 34px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  outline: none;
}

.postal-search-input:focus {
  border-color: #007aff;
  box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
}

.postal-search-button {
  width: 34px;
  height: 34px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  background: #ffffff;
  color: #007aff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.postal-search-button:disabled {
  cursor: not-allowed;
  opacity: 0.65;
}

::v-deep .leaflet-top.leaflet-right .leaflet-control-zoom {
  margin-top: 52px;
}

.fullscreen-button {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1000;
  background: white;
  border: 2px solid rgba(0,0,0,0.2);
  border-radius: 4px;
  width: 34px;
  height: 34px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 5px rgba(0,0,0,0.2);
  transition: all 0.2s;
}

.fullscreen-button:hover {
  background: #f4f4f4;
  box-shadow: 0 2px 8px rgba(0,0,0,0.3);
}

.fullscreen-button:active {
  background: #e8e8e8;
}

.map-alert {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

.map-geocoding-progress {
  position: absolute;
  top: 60px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.rotating {
  animation: rotate 2s linear infinite;
}

::v-deep .map-popup {
  font-family: var(--f7-font-family);
  max-width: 300px;
}

::v-deep .map-popup h3 {
  margin: 0 0 10px 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

::v-deep .map-popup .popup-fields {
  margin-bottom: 10px;
}

::v-deep .map-popup .popup-field {
  margin: 5px 0;
  font-size: 13px;
  line-height: 1.4;
}

::v-deep .map-popup .popup-field strong {
  color: #666;
}

::v-deep .map-popup .popup-address {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid #eee;
  font-size: 12px;
  color: #666;
}

::v-deep .leaflet-popup-content {
  padding: 15px;
  border-radius: 8px;
}

::v-deep .leaflet-popup-close-button {
  font-size: 20px;
  padding: 5px 10px;
}

/* Estilos para las acciones del popup */
::v-deep .map-popup .popup-actions {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #eee;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

::v-deep .popup-actions-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

::v-deep .popup-action-link {
  display: inline-block;
  padding: 6px 12px;
  background-color: #007AFF;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: background-color 0.2s;
}

::v-deep .popup-action-link:hover {
  background-color: #0051D5;
  text-decoration: none;
}

::v-deep .popup-action-link:active {
  background-color: #003DA3;
}

.items-sin-direccion-sheet {
  height: 70vh;
}

::v-deep .items-sin-direccion-sheet .sheet-modal-inner {
  border-radius: 15px 15px 0 0;
}
</style>
